/**
 * Desmembrar uma peça do ambiente: o gestor escreve o nome dela ("porta do
 * armário") e ela passa a andar sozinha pelos setores.
 *
 * A peça NASCE HERDANDO prioridade e prazos do ambiente (`herdarDoAmbiente`,
 * em `dados/pecas.js`) — o formulário já vem preenchido com eles e diz que são
 * herdados, para o gestor entender o que vai gravar antes de alterar. Também
 * nasce no mesmo lugar do ambiente dentro do setor (subsetor/posto), porque é
 * de lá que a peça está saindo.
 */
import React, { useEffect, useMemo, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { herdarDoAmbiente } from "../dados/pecas";
import { CampoColaborador, CampoPrioridade, CamposLugar, limparEspacos } from "./formularios";
import { Acoes, Ajuda, Campo, Campos, Secao } from "./styles";
import { ErroDaAcao, useErroDeAcao } from "./ui";

const TAMANHO_NOME = 160;

function formularioInicial(demanda) {
  const herdado = herdarDoAmbiente(demanda);
  return {
    nome: "",
    prioridade: herdado.prioridade,
    prevista: herdado.data_prevista || "",
    limite: herdado.data_limite || "",
    subsetorId: demanda?.subsetorId || null,
    postoId: demanda?.postoId || null,
    colaboradorId: null,
  };
}

/** Corpo do POST /pecas a partir do formulário. */
export function dadosDaNovaPeca(demanda, setorId, form) {
  return {
    ambiente_chave: demanda?.chave || "",
    ambiente_rotulo: demanda?.rotulo || "",
    nome: limparEspacos(form.nome),
    setor_id: setorId,
    subsetor_id: form.subsetorId || null,
    posto_id: form.postoId || null,
    colaborador_id: form.colaboradorId || null,
    prioridade: form.prioridade,
    data_prevista: form.prevista || null,
    data_limite: form.limite || null,
  };
}

/**
 * Props: aberto, demanda (combinada), setorId, estado, acoes, onFechar,
 * onCriada(peca), onRecarregar.
 */
export default function DesmembrarPeca({ aberto, demanda, setorId, estado, acoes, onFechar, onCriada, onRecarregar }) {
  const [form, setForm] = useState(() => formularioInicial(demanda));
  const { erro, rodar, limpar } = useErroDeAcao();
  const quem = demanda?.chaveLinha || "";
  useEffect(() => {
    if (aberto) {
      setForm(formularioInicial(demanda));
      limpar();
    }
    // `demanda` muda a cada leitura do estado (15 s): recomeçar por causa dela
    // apagaria o nome que a pessoa está digitando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, quem, limpar]);

  const herdado = useMemo(() => herdarDoAmbiente(demanda), [demanda]);
  const semNome = !limparEspacos(form.nome);
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const mudouPrazo = form.prevista !== (herdado.data_prevista || "") || form.limite !== (herdado.data_limite || "");

  const criar = () =>
    rodar(async () => {
      const peca = await acoes.desmembrarPeca(dadosDaNovaPeca(demanda, setorId, form));
      onCriada(peca);
      return peca;
    });

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Desmembrar peça"
      subtitle={demanda ? `De ${demanda.rotulo || demanda.ambiente}` : ""}
      width="min(96vw, 560px)"
      footer={
        <Acoes data-fim="sim">
          <button type="button" className="btn btn--secondary" onClick={onFechar}>
            Cancelar
          </button>
          <AsyncButton silentError disabled={semNome} successMessage="Peça desmembrada." onClick={criar}>
            Desmembrar peça
          </AsyncButton>
        </Acoes>
      }
    >
      <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />
      <Secao aria-label="Dados da peça">
        <Campo>
          Nome da peça
          <input
            value={form.nome}
            maxLength={TAMANHO_NOME}
            onChange={mudar("nome")}
            onBlur={() => setForm((f) => ({ ...f, nome: limparEspacos(f.nome) }))}
            placeholder="Ex.: porta do armário, tampo da bancada"
          />
        </Campo>
        <Ajuda>
          A peça sai do ambiente e passa a andar sozinha: tem setor, status, responsável, prazos e tarefas
          próprios. Ela pode voltar para o ambiente depois.
        </Ajuda>
      </Secao>

      <Secao aria-label="Herdado do ambiente">
        <h3>Herdado do ambiente</h3>
        <CampoPrioridade
          valor={form.prioridade}
          onChange={(prioridade) => setForm((f) => ({ ...f, prioridade: prioridade || f.prioridade }))}
          ajuda={
            demanda?.origemPrioridade === "app"
              ? "Veio da prioridade que alguém definiu no ambiente."
              : "O ambiente não tem prioridade escolhida: esta foi estimada pelo prazo dele."
          }
        />
        <Campos $min={160}>
          <Campo>
            Previsão de término
            <input type="date" value={form.prevista} onChange={mudar("prevista")} />
          </Campo>
          <Campo>
            Data limite
            <input type="date" value={form.limite} onChange={mudar("limite")} />
          </Campo>
        </Campos>
        <Ajuda>
          {mudouPrazo
            ? "Prazos alterados: valem os que estão aqui, não os do ambiente."
            : "Prazos iguais aos do ambiente. Mude se a peça tiver prazo próprio."}
        </Ajuda>
      </Secao>

      <Secao aria-label="Onde a peça começa">
        <h3>Onde a peça começa</h3>
        <CamposLugar
          setorId={setorId}
          estado={estado}
          subsetorId={form.subsetorId}
          postoId={form.postoId}
          onChange={(lugar) => setForm((f) => ({ ...f, ...lugar }))}
        />
        <CampoColaborador
          setorId={setorId}
          colaboradores={estado?.colaboradores}
          valor={form.colaboradorId}
          onChange={(colaboradorId) => setForm((f) => ({ ...f, colaboradorId }))}
          rotulo="Responsável pela peça"
        />
      </Secao>
    </SlideOverPanel>
  );
}
